import type { AcousticSceneInput, AcousticsResult } from './compute'
import type { WorkerRequest } from './worker'

/**
 * Runs geometry queries in a Web Worker, off the UI/render thread.
 *
 * Requests are throttled (~25 Hz) and coalesced: while the worker is busy only the newest scene is kept.
 * Every request carries a revision; results older than the newest one already delivered are dropped.
 */
export class AcousticsClient {
  private worker: Worker
  private rev = 0
  private delivered = 0
  private busy = false
  private pending: AcousticSceneInput | null = null
  private lastSent = 0
  private timer: ReturnType<typeof setTimeout> | null = null
  private disposed = false

  constructor(
    private onResult: (r: AcousticsResult) => void,
    private minIntervalMs = 40,
  ) {
    this.worker = new Worker(new URL('./worker.ts', import.meta.url), { type: 'module' })
    this.worker.onmessage = (e: MessageEvent<AcousticsResult>) => {
      this.busy = false
      if (!this.disposed && e.data.rev > this.delivered) {
        this.delivered = e.data.rev
        this.onResult(e.data)
      }
      if (this.pending) this.schedule()
    }
  }

  /** Queue the latest scene for evaluation. */
  request(scene: AcousticSceneInput) {
    this.pending = scene
    this.schedule()
  }

  private schedule() {
    if (this.disposed || this.busy || this.timer) return
    const wait = Math.max(0, this.lastSent + this.minIntervalMs - performance.now())
    this.timer = setTimeout(() => {
      this.timer = null
      this.flush()
    }, wait)
  }

  private flush() {
    if (this.disposed || this.busy || !this.pending) return
    const scene = this.pending
    this.pending = null
    this.busy = true
    this.lastSent = performance.now()
    const msg: WorkerRequest = { rev: ++this.rev, scene }
    this.worker.postMessage(msg)
  }

  dispose() {
    this.disposed = true
    if (this.timer) clearTimeout(this.timer)
    this.worker.terminate()
  }
}
